import { Dispatch, SetStateAction, useEffect, useLayoutEffect } from "react";
import { useCombobox } from "downshift";
import SearchIcon from "../icons/AikaIcons/SearchIcon";
import useDebounce from "../../hooks/useDebounce";
import { FieldInputProps } from "formik";
import { createPortal } from "react-dom";
import {
  useFloating,
  flip,
  size,
  // detectOverflow,
  autoUpdate,
} from "@floating-ui/react";
import { AutoCompleteItemBase } from "../../interfaces/common";

export type AutoCompleteOnSelectedItemChange<T> = ({
  selectedItem,
  inputValue,
}: {
  selectedItem: T | any;
  inputValue?: string | undefined;
}) => void;

export function AutoComplete<T extends AutoCompleteItemBase>(props: {
  inputId?: string;
  items: T[];
  itemToString: (item: T | null) => string;
  itemToElement: (item: T) => JSX.Element;
  loading: boolean;
  onSelectedItemChange: AutoCompleteOnSelectedItemChange<T>;
  setInputValue: Dispatch<SetStateAction<string>>;
  placeholder?: string;
  className?: string;
  fieldInputProps: FieldInputProps<any>;
  isInvalid?: boolean;
}) {
  const {
    inputId,
    items,
    setInputValue,
    loading,
    onSelectedItemChange,
    itemToString,
    placeholder,
    className,
    itemToElement,
    fieldInputProps,
    isInvalid,
  } = props;
  const {
    isOpen,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    inputValue,
  } = useCombobox({
    defaultHighlightedIndex: 0,
    items,
    itemToString,
    onSelectedItemChange: ({ selectedItem, inputValue }) => {
      onSelectedItemChange({ selectedItem, inputValue });
    },
  });

  const debouncedInputValue = useDebounce(inputValue, 500);
  useEffect(() => {
    if (debouncedInputValue) setInputValue(debouncedInputValue);
    else setInputValue("");
  }, [debouncedInputValue, setInputValue]);

  useLayoutEffect(() => {
    if (isOpen) {
      // document.body.style.pointerEvents = "none";
      document.body.style.overflow = "hidden";
    } else {
      // document.body.style.pointerEvents = "auto";
      document.body.style.overflow = "auto";
    }
  }, [isOpen]);

  const { refs, floatingStyles } = useFloating<HTMLDivElement>({
    whileElementsMounted: autoUpdate,
    middleware: [
      flip(),
      // Match the dropdown width to the input. This used to be done by reading
      // refs.reference.current.offsetWidth during render, which is both a ref
      // read during render and stale whenever the reference resizes without a
      // re-render. The size middleware measures on every autoUpdate instead.
      size({
        apply({ rects, elements }) {
          elements.floating.style.width = `${rects.reference.width}px`;
        },
      }),
    ],
  });

  return (
    <div
      className={className ? `c-search ${className}` : "c-search"}
      ref={refs.setReference}
    >
      <span className="c-search__icon text-muted">
        <SearchIcon />
      </span>
      <input
        className={`c-search__input form-control ${
          isInvalid ? "is-invalid" : ""
        }`}
        type={"search"}
        placeholder={placeholder ? placeholder : "Søg og vælg ..."}
        {...getInputProps({
          id: inputId ?? "downshift-input",
          "aria-controls": "downshift-menu",
          "aria-labelledby": "downshift-label",
        })}
        onBlur={fieldInputProps.onBlur}
      ></input>
      {createPortal(
        <ul
          {...getMenuProps({
            id: "downshift-menu",
            "aria-labelledby": "downshift-label",
            ref: refs.setFloating,
          })}
          className="c-search__result"
          style={{
            ...{ display: isOpen ? "block" : "none" },
            ...floatingStyles,
          }}
        >
          {items.map((item, index) => {
            return (
              <li
                {...getItemProps({ item, index } as any)}
                key={`${item.id}${index}`}
                role="button"
                className={`c-dropdown-search-item${
                  highlightedIndex === index ? " test-highlight" : ""
                }`}
              >
                {itemToElement(item)}
              </li>
            );
          })}
          {loading && <div className="py-2 px-3">Søger ...</div>}
        </ul>,
        document.body,
      )}
    </div>
  );
}
export default AutoComplete;
